/* eslint no-param-reassign: "error" */
import React from 'react';
import {
  useNavigate,
} from 'react-router-dom';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  uiActions,
} from 'web-actions';
import {
  getUiState,
} from 'web-selectors';
import {
  Button,
  Select,
  Avatar,
  AddGroup,
  Checkbox,
  ContentWrapper,
} from 'web-components';
import {
  Flex,
  icons,
  Text,
} from 'web-styled';
import {
  OpenedTooltip,
} from 'web-types';

import * as S from './styled';
import Header from '../Header';

interface Props {
  onChecked: { (data: any): void }
  openedTooltips: OpenedTooltip[]
  onRequestAdd: { (): void }
}

const mapState = (state => ({
  groups: coreSelectors.sortedSelectionResourceList('clientGroups')(state),
  addGroupModal: getUiState('addGroupModal')(state),
  searchOptions: coreSelectors.getSearchOptions('clientGroups')(state),
  selectedIds: coreSelectors.getSelectedIds('clientGroups')(state),
}));

const Groups = ({
  openedTooltips,
  onChecked,
  onRequestAdd,
}: Props) => {
  const navigator = useNavigate();
  const {
    groups,
    addGroupModal,
    searchOptions,
    selectedIds,
  } = useSelector(mapState, shallowEqual);

  const dispatch = useDispatch();

  const cancelModal = () => {
    dispatch(
      uiActions.setUiState({
        addGroupModal: false,
      }),
    );
  };

  const handleDeleteGroup = (group: any) => {
    const onConfirmAction = coreActions.requestDeleteClientEntity({
      id: group.id,
      resourceType: 'clientGroups',
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Delete Client Group',
          bodyText: `Are you sure you want to delete Client Group <b>${group.name}</b>?`,
          confirmText: 'Delete',
        },
        onConfirmAction,
      }),
    );
  };

  const handleDeleteItems = () => {
    const onConfirmAction = coreActions.requestDeleteClientItems({
      resourceType: 'clientGroups',
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Delete group(s)',
          bodyText: 'Are you sure you want to delete selected group(s)?',
          confirmText: 'Delete',
        },
        onConfirmAction,
      }),
    );
  };

  return (
    <ContentWrapper
      header={(
        <Header
          items={groups}
          resourceType="clientGroups"
          openedTooltips={openedTooltips}
          onRequestAdd={onRequestAdd}
          onRequestDeleteItems={handleDeleteItems}
        />
      )}
    >
      <Flex
        column
        fillSpace
        width="100%"
      >
        <S.Search>
          <Select
            value=""
            isClearable
            placeholder="Search groups"
            options={searchOptions}
            onChange={(item) => {
              if (item?.value) {
                navigator(`/client_groups/${item.value}/members`);
              }
            }}
          />
        </S.Search>
        <S.ClientsHeader>
          <Text
            size="18px"
            color="#9ea0a1"
            weight={500}
          >
            Groups
          </Text>
        </S.ClientsHeader>
        {groups.length
          ? groups.map(group => (
            <S.Item
              key={group.id}
            >
              <Flex
                fillSpace
                align="center"
                justify="space-between"
              >
                <Flex
                  spacing={10}
                  align="center"
                >
                  <Checkbox
                    checked={selectedIds.includes(group.id)}
                    onChange={() => onChecked(
                      {
                        id: group.id,
                        selectedIds,
                        resourceType: 'clientGroups',
                      },
                    )}
                  />
                  <Avatar
                    width="28px"
                    height="28px"
                    src={group.avatar?.thumb?.url || icons.clientsGroupAvatar}
                  />
                  <S.Name
                    to={`/client_groups/${group.id}/members`}
                  >
                    {group.name}
                  </S.Name>
                </Flex>
                <S.MoveBar>
                  <Flex
                    spacing={10}
                    align="center"
                  >
                    <Button
                      ghost
                      appearance="danger"
                      onClick={() => handleDeleteGroup(group)}
                    >
                      Delete
                    </Button>
                  </Flex>
                </S.MoveBar>
              </Flex>
            </S.Item>
          )) : (
            <S.Empty>
              <img src={icons.folder} alt="" />
              <span>
                You dont have
                <br />
                anything inside this folder.
              </span>
              <Button
                ghost
                appearance="primary"
                icon="fa fa-plus"
                onClick={onRequestAdd}
              >
                Add group
              </Button>
            </S.Empty>
          )}
      </Flex>
      <AddGroup
        isOpen={addGroupModal}
        headerText="Add Group"
        onRequestClose={() => {
          dispatch(
            uiActions.setUiState({
              addGroupModal: false,
            }),
          );
        }}
        onSubmit={(
          values,
          actions,
          file,
        ) => {
          const onSuccess = (group) => {
            dispatch(
              coreActions.requestUploadGroupAvatar({
                file,
                groupId: group.id,
                callbacks: {
                  onSuccess: cancelModal,
                },
              }),
            );
          };
          dispatch(
            coreActions.requestAddClientEntity({
              payload: values,
              resourceType: 'clientGroups',
              callbacks: {
                onSuccess: file ? onSuccess : cancelModal,
              },
            }),
          );
        }}
      />
    </ContentWrapper>
  );
};

export default Groups;
